import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Grid3x3 } from "lucide-react";

function fyOptions() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return [-1, 0, 1].map(off => `${y + off}-${String((y + off + 1) % 100).padStart(2, "0")}`);
}

export default function MonthlyGrid() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [fy, setFy] = useState(fyOptions()[1]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/projects").then(r => { setProjects(r.data); if (r.data.length) setSelectedProject(r.data[0].id); }); }, []);
  useEffect(() => {
    if (!selectedProject) return;
    api.get(`/reports/monthly-grid?project_id=${selectedProject}&fy=${fy}`).then(r => setData(r.data));
  }, [selectedProject, fy]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Reports · Monthly Grid</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">12-Month Phased vs Actual (Apr → Mar)</h1>
        <p className="text-sm text-slate-500 mt-1">Head-wise plan (budget phasing) against paid actuals for each month of the financial year.</p>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Project</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="grid-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Financial Year</Label>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger data-testid="grid-fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>{fyOptions().map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!data ? <div className="text-slate-500 text-sm">Loading…</div> : data.rows.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">No budget lines to grid. Upload a budget first.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10">Head</th>
                  {data.months.map(m => <th key={m} className="px-2 py-2 font-mono text-slate-500" colSpan={2}>{m.slice(5)}</th>)}
                  <th className="px-2 py-2 text-right">Total</th>
                </tr>
                <tr className="border-t">
                  <th className="text-left px-3 py-1 sticky left-0 bg-slate-50 z-10 text-[10px] uppercase text-slate-500">Alloc.</th>
                  {data.months.map(m => (
                    <React.Fragment key={m}>
                      <th className="px-2 py-1 text-[10px] uppercase text-slate-500 font-semibold text-right">Plan</th>
                      <th className="px-2 py-1 text-[10px] uppercase text-emerald-700 font-semibold text-right">Actual</th>
                    </React.Fragment>
                  ))}
                  <th className="px-2 py-1 text-[10px] uppercase text-slate-500 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(row => (
                  <tr key={row.head_id} className="border-b border-slate-100" data-testid={`grid-row-${row.head_id}`}>
                    <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10 border-r">
                      <div>{row.head_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">₹ {row.allocated.toLocaleString()}</div>
                    </td>
                    {data.months.map((m, i) => {
                      const plan = row.phased[i], act = row.actual[i];
                      const over = act > plan * 1.05;
                      return (
                        <React.Fragment key={m}>
                          <td className="px-2 py-2 text-right font-mono text-slate-500">{plan ? Math.round(plan).toLocaleString() : "—"}</td>
                          <td className={`px-2 py-2 text-right font-mono ${over ? "text-red-600 font-semibold" : act > 0 ? "text-emerald-700" : "text-slate-400"}`}>{act ? Math.round(act).toLocaleString() : "—"}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-2 py-2 text-right font-mono font-semibold">₹ {Math.round(row.total_actual).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <div className="mt-4 text-xs text-slate-500 flex items-center gap-4">
        <span><span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-300 mr-1"></span> Actual within plan</span>
        <span><span className="inline-block w-3 h-3 bg-red-100 border border-red-300 mr-1"></span> Actual &gt; 105% of plan (flag)</span>
      </div>
    </div>
  );
}
